import React, { useState, useEffect, useContext } from 'react';
import Sidebar from '../../components/sidebar/Sidebar';
import Navbar from '../../components/navbar/Navbar';
import { AuthContext } from '../../context/AuthContext';
import { useSidebarState } from '../../components/sidebar/SidebarStateContext';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  LinearProgress, 
  Grid, 
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper
} from '@mui/material';
import {
  Storage as StorageIcon,
  CloudUpload as CloudUploadIcon,
  Folder as FolderIcon,
  Description as DescriptionIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  AudioFile as AudioIcon,
  Archive as ArchiveIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import './Storage.scss';

const Storage = () => {
  const { currentUser, currentService } = useContext(AuthContext);
  const { collapsed } = useSidebarState();
  const navigate = useNavigate();
  const [storageData, setStorageData] = useState({
    total: 5000, // 5 TB en GB
    used: 0,
    available: 5000,
    percentage: 0
  });
  const [fileTypes, setFileTypes] = useState([]);
  const [recentFiles, setRecentFiles] = useState([]);

  useEffect(() => {
    // Simular datos de almacenamiento
    loadStorageData();
  }, []);

  const loadStorageData = () => {
    // Simular datos realistas de almacenamiento Firebase
    const usedGB = Math.floor(Math.random() * 800) + 200; // Entre 200-1000 GB usados
    const availableGB = 5000 - usedGB;
    const percentage = (usedGB / 5000) * 100;

    setStorageData({
      total: 5000,
      used: usedGB,
      available: availableGB,
      percentage: percentage
    });

    // Simular tipos de archivos
    setFileTypes([
      { type: 'Documentos', size: Math.floor(usedGB * 0.4), color: '#1976d2', icon: <DescriptionIcon /> },
      { type: 'Imágenes', size: Math.floor(usedGB * 0.3), color: '#388e3c', icon: <ImageIcon /> },
      { type: 'Videos', size: Math.floor(usedGB * 0.15), color: '#f57c00', icon: <VideoIcon /> },
      { type: 'Audio', size: Math.floor(usedGB * 0.05), color: '#7b1fa2', icon: <AudioIcon /> },
      { type: 'Archivos', size: Math.floor(usedGB * 0.1), color: '#5d4037', icon: <ArchiveIcon /> }
    ]);

    // Simular archivos recientes
    setRecentFiles([
      { name: 'documento_legal_001.pdf', size: '2.5 MB', type: 'Documento', date: '2025-01-15' },
      { name: 'foto_vigilancia_001.jpg', size: '1.8 MB', type: 'Imagen', date: '2025-01-15' },
      { name: 'video_incidente_001.mp4', size: '45.2 MB', type: 'Video', date: '2025-01-14' },
      { name: 'audio_grabacion_001.mp3', size: '3.1 MB', type: 'Audio', date: '2025-01-14' },
      { name: 'archivo_comprimido_001.zip', size: '12.7 MB', type: 'Archivo', date: '2025-01-13' }
    ]);
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStorageStatus = () => {
    if (storageData.percentage < 70) {
      return { status: 'success', message: 'Almacenamiento disponible', icon: <CheckCircleIcon /> };
    } else if (storageData.percentage < 90) {
      return { status: 'warning', message: 'Almacenamiento moderado', icon: <WarningIcon /> };
    } else {
      return { status: 'error', message: 'Almacenamiento crítico', icon: <WarningIcon /> };
    }
  };

  const storageStatus = getStorageStatus();

  // Agregar una clase basada en el estado de colapso
  const containerClass = collapsed ? "listContainer collapsed" : "listContainer";

  return (
    <div className="list">
      <Sidebar />
      <div className={containerClass}>
        <Navbar />
        
        <div className="storage-content">
          <div className="page-header">
            <Typography variant="h4" component="h1" className="page-title">
              <StorageIcon className="title-icon" />
              Almacenamiento y Archivos
            </Typography>
            <Typography variant="body1" color="textSecondary" className="page-subtitle">
              Gestión de capacidad de almacenamiento y archivos del sistema
            </Typography>
          </div>

          {/* Información de capacidad */}
          <Grid container spacing={3} className="storage-grid">
            <Grid item xs={12} md={8}>
              <Card className="storage-card">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Capacidad de Almacenamiento
                  </Typography>
                  
                  <Box className="storage-overview">
                    <Box className="storage-info">
                      <Typography variant="h3" className="storage-total">
                        {storageData.total} GB
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Capacidad Total
                      </Typography>
                    </Box>
                    
                    <Box className="storage-details">
                      <Box className="storage-item">
                        <Typography variant="h6" color="primary">
                          {storageData.used} GB
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Usado
                        </Typography>
                      </Box>
                      
                      <Box className="storage-item">
                        <Typography variant="h6" color="success.main">
                          {storageData.available} GB
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Disponible
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box className="storage-progress">
                    <LinearProgress 
                      variant="determinate" 
                      value={storageData.percentage} 
                      className="progress-bar"
                      sx={{ height: 12, borderRadius: 6 }}
                    />
                    <Typography variant="body2" className="progress-text">
                      {storageData.percentage.toFixed(1)}% utilizado
                    </Typography>
                  </Box>

                  <Box className="storage-status">
                    <Chip
                      icon={storageStatus.icon}
                      label={storageStatus.message}
                      color={storageStatus.status}
                      variant="outlined"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card className="storage-card">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Resumen del Sistema
                  </Typography>
                  
                  <List className="storage-summary">
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Capacidad Total"
                        secondary="5,000 GB (5 TB)"
                      />
                    </ListItem>
                    
                    <ListItem>
                      <ListItemIcon>
                        <InfoIcon color="info" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Almacenamiento Mínimo"
                        secondary="850 GB (Requisito)"
                      />
                    </ListItem>
                    
                    <ListItem>
                      <ListItemIcon>
                        <StorageIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Tipo de Almacenamiento"
                        secondary="Firebase Storage"
                      />
                    </ListItem>
                    
                    <ListItem>
                      <ListItemIcon>
                        <CloudUploadIcon color="secondary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Backup Automático"
                        secondary="Habilitado"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Tipos de archivos */}
          <Grid container spacing={3} className="file-types-grid">
            <Grid item xs={12}>
              <Card className="storage-card">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Distribución por Tipo de Archivo
                  </Typography>
                  
                  <Grid container spacing={2}>
                    {fileTypes.map((fileType, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Paper className="file-type-card" elevation={2}>
                          <Box className="file-type-header">
                            <Box className="file-type-icon" style={{ color: fileType.color }}>
                              {fileType.icon}
                            </Box>
                            <Typography variant="h6" className="file-type-name">
                              {fileType.type}
                            </Typography>
                          </Box>
                          
                          <Typography variant="h4" className="file-type-size" style={{ color: fileType.color }}>
                            {fileType.size} GB
                          </Typography>
                          
                          <Typography variant="body2" color="textSecondary">
                            {((fileType.size / storageData.used) * 100).toFixed(1)}% del total usado
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Archivos recientes */}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card className="storage-card">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Archivos Recientes
                  </Typography>
                  
                  <List className="recent-files-list">
                    {recentFiles.map((file, index) => (
                      <ListItem key={index} className="recent-file-item">
                        <ListItemIcon>
                          <FolderIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={file.name}
                          secondary={`${file.size} • ${file.type} • ${file.date}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Información adicional */}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card className="storage-card">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Características del Almacenamiento
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Box className="feature-item">
                        <Typography variant="subtitle1" color="primary">
                          ✓ Firebase Storage
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Almacenamiento en la nube de Google con alta disponibilidad
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Box className="feature-item">
                        <Typography variant="subtitle1" color="primary">
                          ✓ Capacidad Escalable
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          El sistema puede expandirse hasta 5 TB según las necesidades
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Box className="feature-item">
                        <Typography variant="subtitle1" color="primary">
                          ✓ Redundancia y Backup
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Copias de seguridad automáticas y redundancia global
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Box className="feature-item">
                        <Typography variant="subtitle1" color="primary">
                          ✓ Acceso Seguro
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Encriptación de datos y control de acceso con Firebase Auth
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Box className="feature-item">
                        <Typography variant="subtitle1" color="primary">
                          ✓ Monitoreo en Tiempo Real
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Seguimiento continuo del uso de almacenamiento
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Box className="feature-item">
                        <Typography variant="subtitle1" color="primary">
                          ✓ CDN Global
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Distribución global de contenido para acceso rápido
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </div>
      </div>
    </div>
  );
};

export default Storage;
